import { Helmet } from 'react-helmet-async';
import ContactForm from '../components/ContactForm';
import { companyInfo, openingHours } from '../data/siteData';

export default function GoldPage() {
  const goldCategories = [
    {
      title: "Guld & guldsmykker",
      detail: "Ringe, halskæder, armbånd, øreringe og vedhæng i alle karat (8k, 14k, 18k, 21k, 22k, 24k).",
    },
    {
      title: "Sølv & sølvtøj",
      detail: "Sølvsmykker (925s), tretårnet sølv, bestik, lysestager, korpus og fade.",
    },
    {
      title: "Defekte smykker – opkøbes uanset stand",
      detail: "Knækkede kæder, skæve ringe, enkelte øreringe eller beskadigede smykker. Standen har ingen betydning – vi opkøber også, selvom det er beskidt eller itu.",
    },
    {
      title: "Mønter & barrer",
      detail: "Guld- og sølvmønter, guldbarrer, jubilæumsmønter og ældre samlinger.",
    },
    {
      title: "Arvestykker & samlinger",
      detail: "Komplette smykkeskrin eller boer, hvor du ønsker en samlet, professionel vurdering.",
    },
    {
      title: "Tandguld",
      detail: "Rengjort tandguld vurderes fagligt efter vægt og guldindhold.",
    }
  ];

  return (
    <>
      <Helmet>
        <title>Vi køber guld & sølv - Brønshøj Ure & Guld</title>
        <meta
          name="description"
          content="Vi køber guld og sølv i Brønshøj. Få en uforpligtende vurdering af dit guld, sølv og gamle smykker hos din lokale guldsmed og urmager."
        />
        <meta property="og:title" content="Vi køber guld & sølv - Brønshøj Ure & Guld" />
        <meta
          property="og:description"
          content="Vi køber guld og sølv i Brønshøj. Få en uforpligtende vurdering af dit guld, sølv og gamle smykker hos din lokale guldsmed og urmager."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/807b5052-bea6-494e-8947-b0c29529a13c/img-20250925-wa0002-3.jpg"
        />
      </Helmet>

      {/* Page Header */}
      <section className="bg-[#0B132B] text-white pt-36 sm:pt-40 lg:pt-52 pb-16 sm:pb-20 border-b border-white/10 text-center">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <span className="text-xs uppercase tracking-[0.3em] text-[#B5A897] font-semibold block">
            FORTROLIG & UFORPLIGTENDE VURDERING
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl uppercase tracking-[0.2em] font-normal text-white">
            VI KØBER GULD & SØLV<br />TIL HØJE DAGSPRISER
          </h1>
          <p className="text-xs sm:text-sm text-stone-300 max-w-2xl mx-auto uppercase tracking-widest font-sans leading-relaxed">
            Hos Brønshøj Ure & Guld tilbyder vi altid en fortrolig og uforpligtende vurdering af dit guld og sølv i trygge rammer.
          </p>

          <div className="pt-6 flex flex-col sm:flex-row justify-center items-center gap-4">
            <a
              href={`tel:${companyInfo.phoneRaw}`}
              className="w-full max-w-[280px] sm:w-auto text-center px-4 sm:px-8 py-3.5 border border-white/40 text-white bg-transparent hover:bg-white hover:text-[#0B132B] transition-all text-[11px] sm:text-xs tracking-[0.18em] sm:tracking-[0.25em] uppercase font-medium rounded-none whitespace-nowrap"
            >
              RING DIREKTE: {companyInfo.phone}
            </a>
            <a
              href="#kontakt-sektion"
              className="w-full max-w-[280px] sm:w-auto text-center px-4 sm:px-8 py-3.5 border border-[#B5A897] text-[#B5A897] bg-transparent hover:bg-[#B5A897] hover:text-[#0B132B] transition-all text-[11px] sm:text-xs tracking-[0.18em] sm:tracking-[0.25em] uppercase font-medium rounded-none whitespace-nowrap"
            >
              BESTIL VURDERING
            </a>
          </div>
        </div>
      </section>

      {/* Strømlinet indhold: Hvad vi køber & vilkår i harmonisk to-kolonne */}
      <section className="py-16 sm:py-24 bg-[#F9F8F6]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Venstre kolonne: Introduktion og klare retningslinjer */}
            <div className="lg:col-span-5 space-y-6">
              <div className="space-y-3">
                <div className="w-8 h-[2px] bg-[#B5A897]"></div>
                <span className="text-[11px] uppercase tracking-[0.25em] text-[#B5A897] font-semibold block">
                  OPKØB AF ÆDELMETALLER
                </span>
                <h2 className="font-serif text-2xl sm:text-3xl font-normal uppercase tracking-[0.15em] text-[#1A1A1A]">
                  FÅ MERE UD AF DINE GAMLE SMYKKER
                </h2>
              </div>

              <div className="text-sm text-stone-700 leading-relaxed space-y-4 font-sans">
                <p>
                  Har du gammelt guld, sølv eller arvestykker liggende i skufferne, som du ikke længere bruger? Kom ind i butikken og få en uforpligtende vurdering af værdien. Vi afregner altid ud fra gældende høje dagspriser.
                </p>
                <p>
                  Hvad end der er tale om arvede vielsesringe, knækkede guldkæder, ældre mønter eller tretårnet sølvtøj, kan det hurtigt omsættes til kontante værdier på en tryg og gennemskuelig måde.
                </p>
              </div>

              {/* Vigtig information integreret roligt uden tunge advarselskasser */}
              <div className="pt-4 border-t border-[#E2DDD3] space-y-3">
                <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1A1A1A] flex items-center gap-2">
                  <i className="fa-solid fa-circle-info text-[#B5A897]" aria-hidden="true" />
                  Vigtigt vedrørende opkøb
                </h3>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Vi opkøber alt guld og sølv i alle lødigheder.
                </p>
              </div>
            </div>

            {/* Højre kolonne: De 6 kategorier opstillet roligt på en stribe */}
            <div className="lg:col-span-7">
              <div className="space-y-3 mb-6">
                <span className="text-[11px] uppercase tracking-[0.25em] text-[#B5A897] font-semibold block">
                  HVAD KAN DU SÆLGE?
                </span>
                <h3 className="font-serif text-xl sm:text-2xl font-normal uppercase tracking-[0.15em] text-[#1A1A1A]">
                  EKSEMPLER PÅ OPKØB
                </h3>
              </div>

              <div className="divide-y divide-[#E2DDD3] border-y border-[#E2DDD3]">
                {goldCategories.map((cat, idx) => (
                  <div key={idx} className="py-4 sm:py-5 flex items-start gap-4 group">
                    <span className="text-xs font-serif text-[#B5A897] pt-0.5 tracking-wider font-medium">
                      0{idx + 1}
                    </span>
                    <div className="space-y-1">
                      <h4 className="font-serif text-base sm:text-lg font-normal uppercase tracking-wider text-[#1A1A1A]">
                        {cat.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-stone-600 leading-relaxed font-sans">
                        {cat.detail}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 3 Enkle trin – Design Language Specification: Nordic Luxury / Atelier */}
      <section className="py-12 sm:py-16 bg-[#F9F8F6]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#090e1a] text-white py-14 sm:py-18 lg:py-20 px-6 sm:px-10 lg:px-12 font-sans border border-white/[0.08]">
            <div className="text-center">
              {/* Section Header */}
              <header className="mb-12 lg:mb-14">
                <p className="text-xs font-medium tracking-[0.25em] uppercase text-[#cbb493] mb-3">
                  Tryg & Gennemskuelig Proces
                </p>
                <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl uppercase tracking-wider text-white max-w-xl mx-auto leading-snug">
                  Hvordan foregår en vurdering i butikken?
                </h2>
              </header>

              {/* 3-Column Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Step 01 */}
                <article className="flex flex-col items-center p-8 lg:p-10 bg-[#0e1526]/60 border border-white hover:border-[#cbb493] transition-colors">
                  <span className="font-serif text-xl tracking-widest text-[#cbb493] mb-4">01</span>
                  <h3 className="font-serif text-base sm:text-lg font-normal uppercase tracking-wider text-white mb-3">
                    Besøg Os
                  </h3>
                  <p className="text-xs md:text-[13px] leading-relaxed text-slate-300">
                    Tag dit guld eller sølv med ned i vores forretning på Frederikssundsvej 306A i Brønshøj. Ingen tidsbestilling nødvendig.
                  </p>
                </article>

                {/* Step 02 */}
                <article className="flex flex-col items-center p-8 lg:p-10 bg-[#0e1526]/60 border border-white hover:border-[#cbb493] transition-colors">
                  <span className="font-serif text-xl tracking-widest text-[#cbb493] mb-4">02</span>
                  <h3 className="font-serif text-base sm:text-lg font-normal uppercase tracking-wider text-white mb-3">
                    Test & Vægt
                  </h3>
                  <p className="text-xs md:text-[13px] leading-relaxed text-slate-300">
                    Vi tester metallets finhed/karat og vejer emnerne på en præcisionsvægt direkte foran dig.
                  </p>
                </article>

                {/* Step 03 */}
                <article className="flex flex-col items-center p-8 lg:p-10 bg-[#0e1526]/60 border border-white hover:border-[#cbb493] transition-colors">
                  <span className="font-serif text-xl tracking-widest text-[#cbb493] mb-4">03</span>
                  <h3 className="font-serif text-base sm:text-lg font-normal uppercase tracking-wider text-white mb-3">
                    Tilbud & Salg
                  </h3>
                  <p className="text-xs md:text-[13px] leading-relaxed text-slate-300">
                    Du modtager et uforpligtende tilbud baseret på gældende dagspris. Accepterer du, udbetales beløbet straks.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kontakt / Spørgsmål – Tidligere 2-kolonne opbygning med infobokse og formular */}
      <section id="kontakt-sektion" className="py-16 sm:py-24 bg-white border-t border-[#E2DDD3]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            
            {/* Left Sidebar: Info Card */}
            <aside className="lg:col-span-5 bg-[#F2EFE9] border border-[#E2DDD3] p-8 md:p-12 text-center rounded-none shadow-none">
              <div className="w-px h-8 bg-stone-400 mx-auto mb-6"></div>
              <h2 className="font-serif text-2xl tracking-[0.15em] uppercase text-stone-900 leading-snug mb-4">
                HAR DU SPØRGSMÅL OM VURDERING?
              </h2>
              <p className="text-sm text-stone-600 leading-relaxed max-w-xs mx-auto mb-8 font-sans">
                Du er altid velkommen til at kontakte os på telefon eller sende en forespørgsel.
              </p>
              
              <div className="border-t border-[#E2DDD3] pt-6 space-y-6 text-center">
                <div>
                  <span className="block text-[11px] tracking-[0.2em] uppercase text-[#B5A897] font-semibold mb-1">
                    TELEFON
                  </span>
                  <a
                    href={`tel:${companyInfo.phoneRaw}`}
                    className="text-base font-semibold tracking-wider text-stone-900 hover:text-[#B5A897] transition-colors"
                  >
                    38 28 10 74
                  </a>
                </div>

                <div>
                  <span className="block text-[11px] tracking-[0.2em] uppercase text-[#B5A897] font-semibold mb-1">
                    ADRESSE
                  </span>
                  <address className="not-italic text-sm text-stone-900 tracking-wider font-medium">
                    Frederikssundsvej 306A, 2700 Brønshøj
                  </address>
                </div>

                <div className="pt-2">
                  <a
                    href="https://maps.google.com/maps?q=Frederikssundsvej+306A+2700+Brønshøj"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block border border-stone-900 bg-transparent text-stone-900 py-3.5 px-6 text-xs font-medium tracking-[0.2em] uppercase transition-all duration-200 hover:bg-stone-900 hover:text-white rounded-none cursor-pointer w-full"
                  >
                    ÅBN I GOOGLE MAPS
                  </a>
                </div>
              </div>
            </aside>

            {/* Right Content: Form Card */}
            <main className="lg:col-span-7">
              <ContactForm variant="form" />
            </main>

          </div>

        </div>
      </section>
    </>
  );
}
